import React, { ElementType, FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import useTranslation from '../../hooks/useTranslation'
import NavLink, { NavLinkStatus } from '../../entities/NavLink'
import Logo from '../logo'
import ArrowLeftIcon from '../../assets/media/icons/arrow-left-small.svg'
import ArrowDownIcon from '../../assets/media/icons/arrow-down-small.svg'
import ArrowUpIcon from '../../assets/media/icons/arrow-up-small.svg'
import ListIcon from '../../assets/media/icons/list.svg'
import CompareModelIcon from '../../assets/media/icons/compare-models.svg'
import LastConfigurationsIcon from '../../assets/media/icons/last-configurations.svg'
import UserGarageIcon from '../../assets/media/icons/user-garage.svg'
import style from './navigation.css'
import {
    EQUIPMENT_GROUP_STATUS_ERRONEOUS,
    EQUIPMENT_GROUP_STATUS_NONE,
    EQUIPMENT_GROUP_STATUS_OK,
    EQUIPMENT_GROUP_STATUS_INCOMPLETE,
    EQUIPMENT_GROUP_STATUS_UNAVAILABLE,
} from '../../entities/EquipmentGroup'
import MiwiFeatureAppLoader from '../miwi-feature-app-loader'
import E3DButtonContainer from '../e3d-components/e3d-button'
import E3DVoip from '../e3d-components/e3d-voip/E3DVoip'
import E3DLogoutButton from '../e3d-components/e3d-logout-button/E3DLogoutButton'
import { ROUTE_COMPARE, ROUTE_GARAGE, ROUTE_LAST_CONFIGURATIONS } from '../../constants/routes'

export type Props = {
    navLinks: NavLink[]
    orderButtonsAreActive: boolean
    abortUrl: string | undefined
    isComparisonButtonVisible: boolean
    isLastConfigurationsButtonVisible: boolean
    isUserGarageButtonVisible: boolean
    isTrampolineButtonEnabled: boolean
    is3DExhibitionEnabled: boolean
    configureModelId: string
    isMiwiEnabled: boolean
    isE3DLoggedIn: boolean
    setE3DSharingVisible?: (visible: boolean) => void
}

const Navigation: FunctionComponent<Props> = (props) => {
    const {
        navLinks,
        orderButtonsAreActive,
        abortUrl,
        isComparisonButtonVisible,
        isLastConfigurationsButtonVisible,
        isUserGarageButtonVisible,
        isTrampolineButtonEnabled,
        is3DExhibitionEnabled,
        configureModelId,
        isMiwiEnabled,
        isE3DLoggedIn,
        setE3DSharingVisible,
    } = props
    const { t } = useTranslation()
    const [isBottomMenuOpen, setIsBottomMenuOpen] = useState(configureModelId !== null || configureModelId !== '')

    useEffect(() => {
        if (configureModelId === null || configureModelId === '') {
            setIsBottomMenuOpen(true)
        } else {
            setIsBottomMenuOpen(false)
        }
    }, [configureModelId])

    const createLabelElement = (label: string, isActive: boolean): ReactNode => {
        const labelClassName = isActive ? style.labelActive : style.label

        return (
            <div className={labelClassName}>
                {t(label)}
            </div>
        )
    }

    const createIconElement = (Icon: ElementType): ReactNode => {
        if (Icon == null) {
            return <ListIcon className={style.icon} />
        }

        return <Icon className={style.icon} />
    }

    const createTrafficLightElement = (status: NavLinkStatus): ReactNode => {
        const statusClassMap = {
            [EQUIPMENT_GROUP_STATUS_OK]: style.trafficLightDone,
            [EQUIPMENT_GROUP_STATUS_NONE]: style.trafficLightNone,
            [EQUIPMENT_GROUP_STATUS_INCOMPLETE]: style.trafficLightIncomplete,
            [EQUIPMENT_GROUP_STATUS_ERRONEOUS]: style.trafficLightError,
        }

        return <div className={`${style.trafficLight} ${statusClassMap[status]}`} data-testid={`nav-item-status-${status}`} />
    }

    const linkElements = navLinks.map((link) => {
        const {
            id,
            url,
            navLabel,
            title,
            iconComponent,
            isEnabled,
            isActive,
            status,
            section,
        } = link
        const listItemClassName = style[`listItem-${id}`]
        const formattedTitle = t(title)

        const content = (
            <>
                {createIconElement(iconComponent)}
                {createLabelElement(navLabel, isActive)}
                {createTrafficLightElement(status)}
            </>
        )
        const isDisable = EQUIPMENT_GROUP_STATUS_UNAVAILABLE === status ? style.noClick : ''

        const linkElement = isEnabled ? (
            <Link
                to={url}
                title={formattedTitle}
                className={`${style.link} ${isActive ? style.linkActive : ''} ${isDisable}`}
                data-testid={`nav-item-${id?.replace(/[\\.]/g, '').toLowerCase()}`}
                data-nav-section={section}
            >
                {content}
            </Link>
        ) : <span className={style.linkDisabled}>{content}</span>

        return (
            <li key={id} className={`${style.listItem} ${listItemClassName}`}>
                {linkElement}
            </li>
        )
    })

    const abortLinkItem = (orderButtonsAreActive || isTrampolineButtonEnabled) && abortUrl ? (
        <li className={style.listItem}>
            <a href={abortUrl} className={style.link} data-testid="nav-item-abort">
                {createIconElement(ArrowLeftIcon)}
                {createLabelElement(t('abort.linkLabel'), true)}
                {createTrafficLightElement(undefined)}
            </a>
        </li>
    ) : null

    const comparisonButton = isComparisonButtonVisible ? (
        <li className={style.listItem}>
            <Link to={ROUTE_COMPARE} title="" className={`${style.link}`} data-testid="nav-item-compare">
                {createIconElement(CompareModelIcon)}
                {createLabelElement(t('compare.linkLabel'), true)}
            </Link>
        </li>
    ) : null

    const lastConfigurationsLinkItem = isLastConfigurationsButtonVisible ? (
        <li className={style.listItem}>
            <Link to={ROUTE_LAST_CONFIGURATIONS} title="" className={`${style.link}`} data-testid="nav-item-last-configurations">
                {createIconElement(LastConfigurationsIcon)}
                {createLabelElement(t('garage.lastConfigurations.linkLabel'), true)}
            </Link>
        </li>
    ) : null

    const userGarageLinkItem = isUserGarageButtonVisible ? (
        <li className={style.listItem}>
            <Link to={ROUTE_GARAGE} title="" className={`${style.link}`} data-testid="nav-item-garage">
                {createIconElement(UserGarageIcon)}
                {createLabelElement(t('garage.userGarage.linkLabel'), true)}
            </Link>
        </li>
    ) : null

    const e3DVoipItem = is3DExhibitionEnabled ? (
        <div className={style.voipItem}>
            <E3DVoip />
        </div>
    ) : null

    const e3DExhibitionLinkItem = is3DExhibitionEnabled ? (
        <li className={`${style.listItem} ${style.e3dButtonContainer}`}>
            <E3DButtonContainer setE3DSharingVisible={setE3DSharingVisible || (() => { })} />
        </li>
    ) : null

    const e3DLogoutButton = (is3DExhibitionEnabled && isE3DLoggedIn) ? (
        <li className={`${style.listItem} ${style.logoutButtonContainer}`}>
            <E3DLogoutButton />
        </li>
    ) : null

    const miwiLinkItem = isMiwiEnabled ? (
        <li className={style.listItem}>
            <MiwiFeatureAppLoader />
        </li>
    ) : null

    const bottomLinkList = (
        <ul className={`${style.linkListLast} ${isBottomMenuOpen ? style.linkListLastOpen : ''}`}>
            {e3DExhibitionLinkItem}
            {e3DLogoutButton}
            {abortLinkItem}
            {comparisonButton}
            {lastConfigurationsLinkItem}
            {userGarageLinkItem}
            {miwiLinkItem}
        </ul>
    )

    const bottomMenu =
        bottomLinkList?.props?.children?.filter(Boolean).length > 0 ? (
            <div className={style.bottomMenuContainer}>
                <button
                    className={`${style.menuButton} ${isBottomMenuOpen ? style.menuButtonOpen : ''}`}
                    onClick={() => setIsBottomMenuOpen(!isBottomMenuOpen)}
                    title={t('sidebar.bottomMenu.label')}
                    data-testid="bottom-menu-toggle"
                >
                    <div className={style.menuButtonContent}>
                        <ListIcon
                            className={`${style.menuButtonListIcon} ${isBottomMenuOpen ? style.menuOpenedIcon : ''}`}
                        />
                        <span className={`${style.menuButtonText} ${isBottomMenuOpen ? style.menuOpenedText : ''}`}>
                            {t('sidebar.bottomMenu.label')}
                        </span>
                    </div>
                    {isBottomMenuOpen ? (
                        <ArrowDownIcon
                            className={`${style.menuButtonIcon} ${isBottomMenuOpen ? style.menuOpenedIcon : ''}`}
                        />
                    ) : (
                        <ArrowUpIcon className={style.menuButtonIcon} />
                    )}
                </button>
                {bottomLinkList}
            </div>
        ) : null

    return (
        <nav className={style.container}>
            <Logo />
            <ul className={style.linkList}>
                {linkElements}
            </ul>
            {e3DVoipItem}
            {bottomMenu}
        </nav>
    )
}

export default Navigation
